import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { firstValueFrom, Observable } from 'rxjs';
import { getPageParams, PageRequest, SpringPage } from '@shared/page';
import { Contract, ContractInvoicingStats } from '../models/contract';
import { addHttpParams } from '@shared/utils/request-utils';
import { Moment } from 'moment';
import { AbstractContractService } from './abstract-contract.service';
import { Config, CONFIG } from 'config/config';
import { ClusterUidAndName } from '../models/cluster';
import { SiteMode } from '@shared/services/hostMode.service';
import { CdnStatus } from '@shared/models/site';

@Injectable({
  providedIn: 'root',
})
export class AdminContractService extends AbstractContractService<ContractInvoicingStats> {
  constructor(@Inject(CONFIG) config: Config, http: HttpClient) {
    super(config, http, () => 'admin');
  }

  getContracts(query: ContractQuery, page: PageRequest): Observable<SpringPage<Contract>> {
    let params = addHttpParams({ type: query.type, filter: query.filter }, getPageParams(page));

    return this.http.get<SpringPage<Contract>>(`admin/contracts`, { params });
  }

  async deleteContract(contractRef: ContractNumberOrId): Promise<Object> {
    return firstValueFrom(this.http.delete(`admin/contracts/${contractRef}`));
  }

  addContract(contract: CreateContractRequest): Observable<Contract> {
    return this.http.post<Contract>(`admin/contracts`, contract);
  }

  updateContract(contractRef: ContractNumberOrId, contract: ContractRequest): Observable<Object> {
    return this.http.put(`admin/contracts/${contractRef}`, contract);
  }

  // Sites

  sites(contractRef: ContractNumberOrId): CollectionApi<ContractSite> {
    return new CollectionApi<ContractSite>(this.http, `admin/contracts/${contractRef}/sites`);
  }

  linkSites(contractRef: ContractNumberOrId, organization: OrganizationCodeOrId): Observable<LinkSitesResponse> {
    return this.http.post<LinkSitesResponse>(`admin/contracts/${contractRef}/sites/add-organization`, { organization });
  }

  // Clusters

  clusters(contractRef: ContractNumberOrId): CollectionApi<ContractCluster> {
    return new CollectionApi<ContractCluster>(this.http, `admin/contracts/${contractRef}/clusters`);
  }

  // Organizations

  organizations(contractIdOrNumber: ContractNumberOrId): CollectionApi<ContractOrganization> {
    return new CollectionApi<ContractOrganization>(this.http, `admin/contracts/${contractIdOrNumber}/organizations`);
  }
}

export type AggregateUnit = 'HOUR' | 'DAY' | 'MONTH';
export type FilterValues = {
  organizations: Array<OrganizationFilterValues>;
  clusters: Array<ClusterFilterValues>;
  tags: Array<string>;
};
export type OrganizationFilterValues = {
  name: string;
  code: string;
};
export type ClusterFilterValues = ClusterUidAndName;

export type DateRange = {
  from: string | Moment;
  to: string | Moment;
};

export type InvoicingDateRange = {
  aggregateUnit: AggregateUnit;
  nbPeriods: number;
}

export class CollectionApi<T> {
  constructor(
    private http: HttpClient,
    private rootUri: string,
  ) {}

  getPage(page: PageRequest): Promise<SpringPage<T>> {
    return firstValueFrom(this.http.get<SpringPage<T>>(this.rootUri, { params: getPageParams(page) }));
  }

  add(item: string): Observable<any> {
    return this.http.put(`${this.rootUri}/${item}`, null);
  }

  remove(item: string): Observable<any> {
    return this.http.delete(`${this.rootUri}/${item}`);
  }
}

export interface ContractRequest {
  name: string;
  type: ContractType;
  holder: {
    code: string;
  };
  startDate?: Date | string;
  endDate?: Date | string;

  millionRequestsPerMonth?: number;
  nbSites?: number;
}

export interface CreateContractRequest extends ContractRequest {
  organizations: string[];
}

export type OrganizationCodeOrId = number | string; // id or code

export type ContractNumberOrId = number | string; // id or number

type LinkSitesResponse = { addedSites: string[] };

export const ContractTypes = ['POC', 'BUNDLE', 'CONSUMPTION', 'FREE', 'NFR'] as const;
export type ContractType = (typeof ContractTypes)[number];

export type ContractQuery = Partial<{
  type: ContractType[];
  filter: string;
}>;

export interface ContractCluster {
  uid: string;
  name: string;
  owner: ResourceOwner;
}

export interface ContractSite {
  domainName: string;
  status: string;
  mode: SiteMode;
  passthroughMode: boolean;
  cdn: string;
  cdnStatus: CdnStatus;
  auditMode: boolean;
  cluster: {
    name: string;
  };
  owner: ResourceOwner;
}

export interface ResourceOwner {
  name: string;
  code: string;
  owner: OrganizationOwner;
}

interface OrganizationOwner {
  firstName: string;
  lastName: string;
  email: string;
}

export interface ContractOrganization {
  name: string;
  code: string;
  owner: OrganizationOwner;
}
